import {
	addMonths,
	addWeeks,
	differenceInCalendarMonths,
	differenceInCalendarWeeks,
	eachWeekOfInterval,
	endOfMonth,
	endOfWeek,
	format,
	isAfter,
	isBefore,
	isEqual,
	isSameDay,
	parseISO,
	setHours,
	setMinutes,
	startOfMonth,
	startOfWeek,
} from "date-fns";
import { useState } from "react";

export const useDateFilter = (initialYear = new Date().getFullYear(), initialMonth = new Date().getMonth()) => {
	const [selectedYear, setSelectedYear] = useState(initialYear);
	const [selectedMonth, setSelectedMonth] = useState(initialMonth);

	const startDate = new Date(selectedYear, selectedMonth, 1);
	const endDate = new Date(selectedYear, selectedMonth + 1, 0);

	return {
		selectedYear,
		setSelectedYear,
		selectedMonth,
		setSelectedMonth,
		startDate,
		endDate,
	};
};

export const formatDate = (dateString: string | null) =>
	dateString ? format(parseISO(dateString), "dd/MM/yyyy") : null;

export const formatDateWithTime = (dateString: string | null) =>
	dateString ? format(parseISO(dateString), "dd/MM/yyyy HH:mm") : null;

export const formatDateCompact = (dateString: string | null) =>
	dateString ? format(parseISO(dateString), "dd/MM HH:mm") : null;

export const formatTime = (dateString: string) => format(parseISO(dateString), "HH:mm");

export const countWeeksBetweenDates = (start: Date, end: Date): number => {
	return differenceInCalendarWeeks(end, start, { weekStartsOn: 1 }); // 1 = Monday
}; // non inclusive

export const countWeeksBetweenDatesInclusive = (start: Date, end: Date): number => {
	return countWeeksBetweenDates(start, end) + 1;
};
export const countMonthsBetweenDates = (start: Date, end: Date): number => {
	return differenceInCalendarMonths(end, start);
}; // non inclusive

export const getWeekStartDate = (date: Date): Date => {
	return startOfWeek(date, { weekStartsOn: 1 });
};

export const getNextWeekStartDate = (date: Date): Date => {
	return startOfWeek(addWeeks(date, 1), { weekStartsOn: 1 });
};

export const getWeekEndDate = (date: Date): Date => {
	return endOfWeek(date, { weekStartsOn: 1 });
};

export const getNextMonthFirstWeekStartDate = (date: Date): Date => {
	const firstOfNextMonth = startOfMonth(addMonths(date, 1));
	return startOfWeek(firstOfNextMonth, { weekStartsOn: 1 });
}

export const isBetweenDates = (date: Date, start: Date, end: Date, options: { inclusive?: boolean } = { inclusive: true }): boolean => {
	const isEqualToStartOrEndDate = isEqual(date, start) || isEqual(date, end);
	const isStrictlyBetween = isAfter(date, start) && isBefore(date, end);
	
	if (options.inclusive) {
		return isEqualToStartOrEndDate || isStrictlyBetween;
	}
	return isStrictlyBetween;
};

export const isDateInCurrentWeek = (date: Date): boolean => {
	const currentWeek = new Date();
	return isBetweenDates(date, getWeekStartDate(currentWeek), getWeekEndDate(currentWeek));
};

export const isDateInPreviousWeek = (date: Date): boolean => {
	const now = new Date();
	return isBetweenDates(date, getPreviousWeekStartDate(now), getPreviousWeekEndDate(now));
};

export const isDateInCurrentMonth = (date: Date): boolean => {
	const currentMonth = new Date();
	return isBetweenDates(date, startOfMonth(currentMonth), endOfMonth(currentMonth));
};

export const isDateInPreviousMonth = (date: Date): boolean => {
	const previousMonth = addMonths(new Date(), -1);
	return isBetweenDates(date, startOfMonth(previousMonth), endOfMonth(previousMonth));
};

export const areDatesEqual = (dateA: string | Date | null, dateB: string | Date | null) => {
	if (!dateA && !dateB) return true;
	if (!dateA || !dateB) return false;
	const parsedA = typeof dateA === "string" ? parseISO(dateA) : dateA;
	const parsedB = typeof dateB === "string" ? parseISO(dateB) : dateB;
	return isEqual(parsedA, parsedB);
};

export const isDateAfter = (dateA: string | Date, dateB: string | Date): boolean => {
	const parsedA = typeof dateA === "string" ? new Date(dateA) : dateA;
	const parsedB = typeof dateB === "string" ? new Date(dateB) : dateB;
	return isAfter(parsedA, parsedB);
};
export const isDateAfterOrSameDay = (dateA: string | Date, dateB: string | Date): boolean => {
	const parsedA = typeof dateA === "string" ? new Date(dateA) : dateA;
	const parsedB = typeof dateB === "string" ? new Date(dateB) : dateB;
	return isAfter(parsedA, parsedB) || isSameDay(parsedA, parsedB);
};

export const isDateBefore = (dateA: string | Date, dateB: string | Date): boolean => {
	const parsedA = typeof dateA === "string" ? new Date(dateA) : dateA;
	const parsedB = typeof dateB === "string" ? new Date(dateB) : dateB;
	return isBefore(parsedA, parsedB);
};

export const isDateBeforeOrSameDay = (dateA: string | Date, dateB: string | Date): boolean => {
	const parsedA = typeof dateA === "string" ? new Date(dateA) : dateA;
	const parsedB = typeof dateB === "string" ? new Date(dateB) : dateB;
	return isBefore(parsedA, parsedB) || isSameDay(parsedA, parsedB);
};

export const getArrayElementByDateProperty = <T>(data: Array<T>, dataDateProperty: keyof T, pickLatest: boolean) => {
	if (!Array.isArray(data)) return null;
	if (data.length === 1) return data[0];
	return data.reduce((selected, current) => {
		const selectedDate = new Date(selected[dataDateProperty] as any).getTime();
		const currentDate = new Date(current[dataDateProperty] as any).getTime();

		if (pickLatest) {
			return currentDate > selectedDate ? current : selected;
		} else {
			return currentDate < selectedDate ? current : selected;
		}
	});
};

export const getEndOfDayDate = (date: string | Date): Date => {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  return setMinutes(setHours(parsedDate, 23), 59);
};

export const isDayMonday = (day: 0 | 1 | 2 | 3 | 4 | 5 | 6) => day === 1;

export const eachWeekOfIntervalWithStartCapped = (
	interval: { start: Date; end: Date },
	options: { weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6; startCapDate: Date }
) => {
	const weeks = eachWeekOfInterval(interval, { weekStartsOn: options.weekStartsOn });
	if (weeks.length === 0) return weeks;
	weeks[0] = options.startCapDate;

	return weeks;
};

export const validateDateString = (value: string) => {
	// validates true for this specific format YYYY/MM/DD like '2024-02-29' or '2025-10-13 23:59:59'
	// In forms use after sanitizeData 
	const dateRegex = /^([1-9][0-9]{3})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])( ([01][0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?)?$/;
	const isValid = dateRegex.test(value);
	return isValid;
};
export const getNextWeekEndDate = (date: Date): Date => {
	return endOfWeek(addWeeks(date, 1), { weekStartsOn: 1 });
};

export const getPreviousWeekStartDate = (date: Date): Date => {
	return startOfWeek(addWeeks(date, -1), { weekStartsOn: 1 });
};

export const getPreviousWeekEndDate = (date: Date): Date => {
	return endOfWeek(addWeeks(date, -1), { weekStartsOn: 1 });
};

